import { memo, useCallback, useEffect, useMemo, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";

import CardMedia from "../../../Media/CardMedia";
import IconButton from "../../../Buttons/IconButton";
import { useTranslationApp } from "../../../../lib/i18next";
import { useMediaDimensions } from "../../../../hooks/useMediaDimensions";
import { getExtensionFile, logoPlatform } from "../../../../utils/string";
import { isCompetitorAdStrongSignal } from "../../../../services/competitorAdsGallery";
import {
  BOX_SHADOW_LITTLE,
  ORANGE_COLOR,
  PRIMARY_COLOR_TEXT,
} from "../../../../utils/colors";
import {
  VIDEO_FORMATS,
  GALLERY_DRAG_MIME_SELECTED,
} from "../../../../utils/constants";
import { GALLERY_VIDEO_THUMBNAIL_FALLBACK_TOOLTIP } from "../../../../i18n/keysTranslations";
import { gallerySecondaryCircleSurfaceSx } from "../../../Gallery/galleryIconButtonStyles";
import {
  getCompetitorItemId,
  hasCompetitorAdLibraryLayout,
  isCompetitorItemViewOnly,
  isCompetitorMediaAllowedForReference,
} from "../utils";
import CompetitorAdLibraryCard from "./CompetitorAdLibraryCard";
import CompetitorDurationBadge from "./CompetitorDurationBadge";
import CompetitorPlatformLabel from "./CompetitorPlatformLabel";
import CompetitorStrongSignalChip from "./CompetitorStrongSignalChip";
import CompetitorItemActions from "./CompetitorItemActions";

const NOOP = () => {};
const DEFAULT_FAVORITE_STATE = { isFavorite: false, isLoading: false };

const VideoThumbnailFallbackAlert = ({ tooltip }) => {
  if (!tooltip) return null;

  return (
    <Tooltip
      title={tooltip}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: 280,
            fontSize: 12,
            lineHeight: 1.4,
          },
        },
      }}
    >
      <Box
        component="span"
        onClick={(event) => event.stopPropagation()}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          cursor: "help",
          color: ORANGE_COLOR,
          flexShrink: 0,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 18 }} />
      </Box>
    </Tooltip>
  );
};

const VideoMuteToggle = ({ isMuted, onToggleMute }) => (
  <Box
    className="gallery-video-mute-toggle"
    sx={{
      position: "absolute",
      inset: 0,
      zIndex: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: 0,
      transition: "opacity 0.2s ease",
      pointerEvents: "none",
    }}
  >
    <Box
      sx={{ pointerEvents: "auto", display: "inline-flex" }}
      onClick={onToggleMute}
    >
      <IconButton
        onClick={onToggleMute}
        aria-pressed={!isMuted}
        type="icon"
        sx={{
          pointerEvents: "auto",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          color: "white",
          width: 50,
          height: 50,
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
          },
          "& .MuiSvgIcon-root": {
            fontSize: 30,
          },
        }}
      >
        {isMuted ? (
          <VolumeOffIcon sx={{ color: "#fff !important" }} />
        ) : (
          <VolumeUpIcon sx={{ color: "#fff !important" }} />
        )}
      </IconButton>
    </Box>
  </Box>
);

const SelectionCheckbox = ({ isSelected, onSelect }) => {
  const theme = useTheme();

  return (
    <Box
      className="gallery-checkbox"
      onClick={(event) => {
        event.stopPropagation();
        onSelect(event);
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: 34,
        height: 34,
        borderRadius: "50%",
        cursor: "pointer",
        transition: "all 0.2s ease",
        ...gallerySecondaryCircleSurfaceSx(theme),
        "&:hover": {
          backgroundColor: `${theme.palette.button.secondary.hover.main} !important`,
        },
      }}
    >
      {isSelected ? (
        <CheckCircleIcon
          sx={{
            color: "#4caf50 !important",
            fontSize: "26px",
            "& path": {
              stroke: "#ffffff",
              strokeWidth: "3px",
              paintOrder: "stroke fill",
            },
          }}
        />
      ) : (
        <RadioButtonUncheckedIcon
          sx={{
            color: `${PRIMARY_COLOR_TEXT} !important`,
            fontSize: "28px",
          }}
        />
      )}
    </Box>
  );
};

const CompetitorGalleryItem = memo(function CompetitorGalleryItem({
  item,
  index,
  isSelected = false,
  folderContentID = null,
  draggableForFolders = false,
  isPartOfDragSelection = false,
  selectedDragCount = 0,
  isCreateWithIAMode = false,
  acceptedReferenceMediaType = null,
  isMenuMode = false,
  hideFavoriteButton = false,
  isUploading = false,
  uploadingItemId = null,
  directAddLabel,
  videoMuteKey,
  unmutedVideoKey = null,
  onRequestVideoUnmute = NOOP,
  onRequestVideoMute = NOOP,
  onSelectItem = NOOP,
  onSelectPreview = NOOP,
  onDirectGenerateItem = NOOP,
  onToggleFavoriteItem = NOOP,
  onCensoredItem = NOOP,
  onOpenExternalAd,
  getFavoriteState = () => DEFAULT_FAVORITE_STATE,
}) {
  const { t } = useTranslationApp();
  const currentUrl = item?.url;
  const isCensored = item?.featureAddonCensored === true;
  const extension = useMemo(() => getExtensionFile(currentUrl), [currentUrl]);

  const isVideo = useMemo(() => {
    if (item?.type === "video") return true;
    if (item?.type === "image") return false;
    if (!currentUrl) return false;
    if (currentUrl.startsWith("data:video/")) return true;
    return VIDEO_FORMATS.includes(extension);
  }, [currentUrl, extension, item?.type]);

  const hasBackendDimensions = !!(item?.width && item?.height);
  const shouldLoadDimensions = !hasBackendDimensions && !!currentUrl;

  const { dimensions, aspectRatio, loading } = useMediaDimensions(
    shouldLoadDimensions ? currentUrl : null
  );

  const resolvedAspectRatio = useMemo(() => {
    if (hasBackendDimensions && item.width && item.height) {
      return item.width / item.height;
    }
    if (dimensions?.width && dimensions?.height) {
      return dimensions.width / dimensions.height;
    }
    return aspectRatio || 1;
  }, [
    aspectRatio,
    dimensions?.height,
    dimensions?.width,
    hasBackendDimensions,
    item?.height,
    item?.width,
  ]);

  const isViewOnlyFromItem = isCompetitorItemViewOnly(item);
  const itemId = getCompetitorItemId(item);
  const { isFavorite } = getFavoriteState(item);
  const isVideoMuted = unmutedVideoKey !== videoMuteKey;
  const showVideoThumbnailFallback = Boolean(item?.isVideoThumbnailFallback);
  const hasDuration = item?.activeDurationHours != null;
  const showStrongSignalChip = isCompetitorAdStrongSignal(
    item?.activeDurationHours
  );
  const platform = item?.platform;
  const hasPlatform = Boolean(platform && logoPlatform(platform));
  const showAdLibraryLayout = hasCompetitorAdLibraryLayout(item);
  const [activeMediaVersion, setActiveMediaVersion] = useState(null);

  const defaultActiveMedia = useMemo(
    () => ({
      url: currentUrl,
      type: isVideo ? "video" : "image",
    }),
    [currentUrl, isVideo]
  );

  const resolvedActiveMedia = showAdLibraryLayout
    ? activeMediaVersion || defaultActiveMedia
    : defaultActiveMedia;

  const activeReferenceType =
    resolvedActiveMedia.type === "video" ? "video" : "image";
  const isRejectedReferenceMedia = isCreateWithIAMode
    ? !isCompetitorMediaAllowedForReference(
        activeReferenceType,
        acceptedReferenceMediaType || "image"
      )
    : showAdLibraryLayout
      ? activeReferenceType === "video"
      : isViewOnlyFromItem;
  const isViewOnly = isRejectedReferenceMedia;
  const isSelectable =
    isCreateWithIAMode &&
    !isRejectedReferenceMedia &&
    !loading &&
    Boolean(item?.id);
  const showPickerCheckbox = isSelectable;
  const showMetaRow =
    showPickerCheckbox ||
    hasDuration ||
    showStrongSignalChip ||
    showVideoThumbnailFallback ||
    hasPlatform;
  const showDirectAddButton = isMenuMode && Boolean(item?.id);

  const onActiveVersionChange = useCallback((version) => {
    setActiveMediaVersion(version);
  }, []);

  useEffect(() => {
    setActiveMediaVersion(null);
  }, [item?.id, item?.url]);

  const onOpenPreviewWithActiveMedia = useCallback(() => {
    onSelectPreview({
      index,
      url: resolvedActiveMedia.url,
      type: resolvedActiveMedia.type,
    });
  }, [
    index,
    onSelectPreview,
    resolvedActiveMedia.type,
    resolvedActiveMedia.url,
  ]);

  const onCardClick = useCallback(
    (event) => {
      if (isViewOnly) return;
      if (event.target.closest(".gallery-checkbox")) return;
      if (event.target.closest(".gallery-direct-add-button")) return;
      if (event.target.closest(".competitor-ad-library-carousel-controls"))
        return;

      if (isCreateWithIAMode) {
        onSelectItem(index, {
          url: resolvedActiveMedia.url,
          type: resolvedActiveMedia.type,
        });
        return;
      }

      onOpenPreviewWithActiveMedia();
    },
    [
      index,
      isCreateWithIAMode,
      isViewOnly,
      onOpenPreviewWithActiveMedia,
      onSelectItem,
      resolvedActiveMedia.type,
      resolvedActiveMedia.url,
    ]
  );

  const onPreviewClick = useCallback(
    (event) => {
      event?.stopPropagation();
      onOpenPreviewWithActiveMedia();
    },
    [onOpenPreviewWithActiveMedia]
  );

  const onToggleFavoriteClick = useCallback(
    (event) => {
      event?.stopPropagation();
      onToggleFavoriteItem({ item, index });
    },
    [index, item, onToggleFavoriteItem]
  );

  const onDirectGenerateClick = useCallback(
    (event) => {
      event?.stopPropagation();
      if (!item?.id || isUploading || uploadingItemId) return;
      onDirectGenerateItem({
        index,
        url: resolvedActiveMedia.url,
        type: resolvedActiveMedia.type,
      });
    },
    [
      index,
      isUploading,
      item?.id,
      uploadingItemId,
      onDirectGenerateItem,
      resolvedActiveMedia.type,
      resolvedActiveMedia.url,
    ]
  );

  const onToggleVideoMute = useCallback(
    (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();

      const video = event?.currentTarget
        ?.closest?.(".gallery-video-mute-toggle")
        ?.parentElement?.querySelector?.("video");

      if (isVideoMuted) {
        if (video) video.muted = false;
        onRequestVideoUnmute({ videoMuteKey });
        return;
      }

      if (video) video.muted = true;
      onRequestVideoMute();
    },
    [isVideoMuted, onRequestVideoMute, onRequestVideoUnmute, videoMuteKey]
  );

  const onSelectCheckbox = useCallback(
    (event) => {
      event?.stopPropagation();
      if (!isSelectable) return;
      onSelectItem(index, {
        url: resolvedActiveMedia.url,
        type: resolvedActiveMedia.type,
      });
    },
    [
      index,
      isSelectable,
      onSelectItem,
      resolvedActiveMedia.type,
      resolvedActiveMedia.url,
    ]
  );

  const isDraggableForFolders = draggableForFolders && Boolean(folderContentID);

  const onDragStartItem = useCallback(
    (event) => {
      if (!isDraggableForFolders || !folderContentID) return;

      event.dataTransfer.setData("text/plain", folderContentID);
      if (isPartOfDragSelection && selectedDragCount > 1) {
        event.dataTransfer.setData(GALLERY_DRAG_MIME_SELECTED, "1");
      }
      event.dataTransfer.effectAllowed = "move";
    },
    [
      folderContentID,
      isDraggableForFolders,
      isPartOfDragSelection,
      selectedDragCount,
    ]
  );

  if (!currentUrl) return null;

  return (
    <Box
      draggable={isDraggableForFolders}
      onDragStart={onDragStartItem}
      onClick={onCardClick}
      sx={{
        boxShadow: BOX_SHADOW_LITTLE,
        borderRadius: 3,
        cursor: isViewOnly ? "default" : "pointer",
        position: "relative",
        zIndex: 0,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        overflow: "hidden",
        filter: isCensored ? "blur(7px)" : "none",
        ...(isDraggableForFolders
          ? {
              cursor: "grab",
            }
          : {}),
        opacity: !isCreateWithIAMode || isSelectable || isViewOnly ? 1 : 0.25,
      }}
    >
      {showMetaRow ? (
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            py: 1,
            px: 1.5,
            borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
            backgroundColor: "#ffffff",
          }}
        >
          {showPickerCheckbox ? (
            <SelectionCheckbox
              isSelected={isSelected}
              onSelect={onSelectCheckbox}
            />
          ) : null}
          {hasPlatform ? <CompetitorPlatformLabel platform={platform} /> : null}
          {showStrongSignalChip ? (
            <CompetitorStrongSignalChip
              activeDurationHours={item.activeDurationHours}
            />
          ) : null}
          {hasDuration ? (
            <CompetitorDurationBadge
              activeDurationHours={item.activeDurationHours}
            />
          ) : null}
          {showVideoThumbnailFallback ? (
            <VideoThumbnailFallbackAlert
              tooltip={t(GALLERY_VIDEO_THUMBNAIL_FALLBACK_TOOLTIP)}
            />
          ) : null}
        </Box>
      ) : null}

      {showAdLibraryLayout ? (
        <CompetitorAdLibraryCard
          item={item}
          isVideoMuted={isVideoMuted}
          onToggleVideoMute={onToggleVideoMute}
          onActiveVersionChange={onActiveVersionChange}
        />
      ) : (
        <Box
          onContextMenu={(event) => event.preventDefault()}
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: resolvedAspectRatio,
            flexShrink: 0,
            overflow: "hidden",
            "& img, & video": {
              WebkitUserDrag: "none",
              userSelect: "none",
              WebkitTouchCallout: "none",
            },
            "&:hover .gallery-video-mute-toggle": {
              opacity: 1,
            },
            "&:hover .gallery-video-mute-toggle .MuiIconButton-root": {
              pointerEvents: "auto",
            },
          }}
        >
          <CardMedia
            url={currentUrl}
            mediaType={isVideo ? "video" : "image"}
            onFailedLoadUrl={NOOP}
            sx={{
              objectFit: "cover",
              borderRadius: 0,
              width: "100%",
              height: "100%",
            }}
            sxVideo={{
              borderRadius: 0,
            }}
            propsType={{
              controls: false,
              autoplay: true,
              muted: isVideoMuted,
              loop: true,
              playsInline: true,
              draggable: false,
              controlsList: "nodownload",
            }}
          />

          {isVideo ? (
            <VideoMuteToggle
              isMuted={isVideoMuted}
              onToggleMute={onToggleVideoMute}
            />
          ) : null}
        </Box>
      )}

      <CompetitorItemActions
        item={item}
        isFavorite={isFavorite}
        hideFavoriteButton={hideFavoriteButton}
        showDirectAddButton={showDirectAddButton}
        directAddLabel={directAddLabel}
        isDirectAddLoading={Boolean(
          uploadingItemId && itemId === uploadingItemId
        )}
        isDirectAddDisabled={
          Boolean(uploadingItemId || isUploading) && itemId !== uploadingItemId
        }
        onPreview={onPreviewClick}
        onToggleFavorite={onToggleFavoriteClick}
        onDirectGenerate={onDirectGenerateClick}
        onOpenExternalAd={onOpenExternalAd}
      />
      {isCensored ? (
        <Box
          component="button"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onCensoredItem(item);
          }}
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            border: 0,
            background: "rgba(255,255,255,0.15)",
            cursor: "pointer",
          }}
        />
      ) : null}
    </Box>
  );
});

export default CompetitorGalleryItem;
